/**
 * SurrogateBanner — sticky top banner that renders when the current
 * session is in Surrogate Mode. Also renders a lightweight "Enter
 * Surrogate Mode" chooser for substitutes with pending, accepted,
 * in-window assignments.
 *
 * Mount once at the router level (App.js). Reads state from
 * localStorage (via lib/surrogate.js) so no context provider is needed.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { UserCog, LogOut, ChevronDown, ShieldCheck } from 'lucide-react';
import {
  getSurrogateState, exitSurrogateMode,
  fetchAvailableSurrogates, enterSurrogateMode, checkSurrogateSelf,
} from '../lib/surrogate';
import { notifyError } from '../lib/errors';

export default function SurrogateBanner() {
  const [{ active, meta }, setState] = useState(() => getSurrogateState());
  const [available, setAvailable] = useState([]);
  const [showChooser, setShowChooser] = useState(false);
  const [busy, setBusy] = useState(false);
  // Guards against firing a second auto-join/auto-exit while one request
  // from a previous poll tick is still in flight.
  const syncingRef = React.useRef(false);

  // Keep local state in sync when the app writes to localStorage from
  // another tab OR from a code path that doesn't reload.
  useEffect(() => {
    const onStorage = () => setState(getSurrogateState());
    window.addEventListener('storage', onStorage);
    // Also poll every 5s for same-tab writes (localStorage events don't
    // fire in the tab that made the change). Cheap.
    const iv = setInterval(onStorage, 5000);
    return () => { window.removeEventListener('storage', onStorage); clearInterval(iv); };
  }, []);

  // Fetch available assignments once whenever we're NOT in surrogate mode
  const reloadAvailable = useCallback(async () => {
    if (active) { setAvailable([]); return; }
    try {
      const res = await fetchAvailableSurrogates();
      setAvailable(res?.available || []);
    } catch { setAvailable([]); }
  }, [active]);

  useEffect(() => { reloadAvailable(); }, [reloadAvailable]);

  const onExit = async () => {
    setBusy(true);
    try {
      await exitSurrogateMode();
      toast.success('Exited Surrogate Mode');
      // Full reload so every in-memory API client picks up the restored token.
      window.location.reload();
    } catch (e) {
      notifyError(e, 'Could not exit');
    } finally { setBusy(false); }
  };

  const onEnter = async (subId) => {
    setBusy(true);
    try {
      const res = await enterSurrogateMode(subId);
      toast.success(`Now acting for ${res.surrogate_for_name}`);
      window.location.reload();
    } catch (e) {
      notifyError(e, 'Could not enter surrogate mode');
    } finally { setBusy(false); }
  };

  // ── Dynamic cross-device sync ────────────────────────────────────
  // Entering/exiting Surrogate Mode on ONE device (say, web) now flips a
  // shared `surrogate_session_active` flag on the substitution row
  // (backend/surrogate_routes.py + auth_middleware.py). Every 5s we check
  // that flag from here too, so:
  //   • not active + someone else already entered  → silently join too
  //   • active + the session was ended elsewhere    → silently exit too
  // No manual re-entry/re-exit needed on a second device or after a
  // plain page refresh.
  useEffect(() => {
    const sync = async () => {
      if (syncingRef.current) return;
      if (active) {
        const res = await checkSurrogateSelf();
        if (!res.ok || !res.surrogate_active) {
          syncingRef.current = true;
          await exitSurrogateMode();
          toast('Surrogate session ended', { description: 'This session was exited from another device.' });
          window.location.reload();
        }
        return;
      }
      const joinable = available.find((a) => a.surrogate_session_active);
      if (joinable) {
        syncingRef.current = true;
        try {
          const res = await enterSurrogateMode(joinable.substitution_id);
          toast.success(`Joined Surrogate Mode — acting for ${res.surrogate_for_name}`);
          window.location.reload();
        } catch { syncingRef.current = false; }
      }
    };
    const iv = setInterval(sync, 5000);
    return () => clearInterval(iv);
  }, [active, available]);

  // ── Active surrogate banner ─────────────────────────────────────
  if (active && meta) {
    return (
      <div
        className="sticky top-0 z-[120] w-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white shadow-md"
        role="alert"
        data-testid="surrogate-banner"
      >
        <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center gap-3 text-[13px] font-semibold">
          <UserCog className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">
            Acting for <strong className="mx-1">{meta.surrogate_for_name || 'Teacher'}</strong>
            {meta.subject_name && (
              <span className="opacity-90 font-normal">
                · subject <em>{meta.subject_name}</em>
              </span>
            )}
            {meta.end_date && (
              <span className="opacity-75 font-normal ml-2">
                until {meta.end_date}
              </span>
            )}
          </span>
          <span className="hidden sm:inline text-[11px] uppercase tracking-widest font-bold bg-white/20 px-2 py-0.5 rounded-full">
            Surrogate Mode
          </span>
          <button
            onClick={onExit}
            disabled={busy}
            className="ml-auto inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 disabled:opacity-50 rounded-full px-3 py-1 text-[12px] font-bold transition-colors"
            data-testid="surrogate-exit-btn"
          >
            <LogOut className="w-3.5 h-3.5" />
            Exit
          </button>
        </div>
      </div>
    );
  }

  // ── Chooser banner (only when the user has accepted, in-window
  //    assignments and is NOT already in surrogate mode). Compact.
  if (available.length > 0) {
    return (
      <>
        <div
          className="sticky top-0 z-[120] w-full bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-600 text-white shadow-md"
          data-testid="surrogate-chooser-banner"
        >
          <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center gap-3 text-[13px] font-semibold">
            <ShieldCheck className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              {available.length === 1
                ? `You have an active substitution for ${available[0].subject_name} (covering ${available[0].original_teacher_name}).`
                : `You have ${available.length} active substitutions ready to enter.`}
            </span>
            <button
              onClick={() => setShowChooser(true)}
              className="ml-auto inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-full px-3 py-1 text-[12px] font-bold transition-colors"
              data-testid="surrogate-open-chooser-btn"
            >
              Enter Surrogate Mode
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {showChooser && (
          <div
            className="fixed inset-0 z-[130] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setShowChooser(false); }}
            data-testid="surrogate-chooser-modal"
          >
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full p-6 border border-slate-200 dark:border-white/10">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">Enter Surrogate Mode</h2>
                  <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">
                    Pick which teacher you&apos;re acting for. You&apos;ll write under their identity for this subject.
                  </p>
                </div>
                <button onClick={() => setShowChooser(false)} className="text-slate-400 hover:text-slate-700 dark:hover:text-white text-xl" aria-label="Close">×</button>
              </div>
              <div className="space-y-2.5 max-h-[60vh] overflow-y-auto">
                {available.map((s) => (
                  <button
                    key={s.substitution_id}
                    onClick={() => onEnter(s.substitution_id)}
                    disabled={busy}
                    className="w-full text-left rounded-xl border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 dark:border-white/10 dark:hover:bg-white/5 p-3.5 transition-colors disabled:opacity-50"
                    data-testid={`surrogate-enter-${s.substitution_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{s.subject_name || 'Subject'}</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          Covering <strong>{s.original_teacher_name}</strong> · {s.class_name}
                        </p>
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300 px-2 py-0.5 rounded-full flex-shrink-0">
                        {s.scope}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">Until {s.end_date}</p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowChooser(false)}
                className="w-full mt-4 py-2.5 rounded-xl text-[13px] font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                data-testid="surrogate-chooser-continue-normal"
              >
                Continue as yourself
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return null;
}
